/**
 * Debug utilities for BabbleGram
 * Handles debug parameters, date overrides, and console logging
 */

import { normalizeDate, formatDate } from "./dateUtils";
import { toast } from "sonner";
import { deleteCookie } from "./cookieUtils";

// Maximum size for debug logs in localStorage (in bytes)
const MAX_DEBUG_LOG_SIZE = 100000; // 100KB

// Debug Parameter Keys
const DEBUG_DATE_KEY = 'debug_date';
const DEBUG_LOGS_KEY = 'debug_logs';
const GAME_DATE_KEY = 'game_persistence_date';

// Debug Parameter Types
type DebugParams = {
  newDay: boolean;
  showLogs: boolean;
  incrementStreak: boolean;
  debug: string | null;
  debugTransitions: boolean;
  forceRefresh: boolean;
};

/**
 * Get debug parameters from URL
 * @returns Object containing debug parameters
 */
export const getDebugParams = (): DebugParams => {
  try {
    if (typeof window === 'undefined') return {
      newDay: false,
      showLogs: false,
      incrementStreak: false,
      debug: null,
      debugTransitions: false,
      forceRefresh: false
    };
    
    const params = new URLSearchParams(window.location.search);
    const debugParam = params.get('debug');
    
    return {
      newDay: debugParam === 'newday',
      showLogs: debugParam === 'logson',
      incrementStreak: debugParam === 'streak',
      debug: debugParam,
      debugTransitions: debugParam === 'transitions' || debugParam === 'all',
      forceRefresh: debugParam === 'forcerefresh'
    };
  } catch (error) {
    console.error('Error getting debug params:', error);
    
    // Return safe defaults if there's an error
    return {
      newDay: false,
      showLogs: false,
      incrementStreak: false,
      debug: null,
      debugTransitions: false,
      forceRefresh: false
    };
  }
};

/**
 * Check if debug mode is active
 * @returns boolean indicating if debug date mode is active
 */
export const isDebugDateMode = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.get('debug') === 'date';
};

// Helpers for robust date parsing and storage
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const parseDateFromStorage = (value: string): { date: Date | null; migrateTo?: string } => {
  try {
    if (DATE_ONLY_REGEX.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      const local = new Date(y, (m as number) - 1, d, 0, 0, 0, 0);
      return { date: normalizeDate(local) };
    }
    // Fallback: ISO or other string – parse and normalize, then migrate to date-only
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return { date: null };
    const local = normalizeDate(parsed);
    return { date: local, migrateTo: formatDate(local) };
  } catch {
    return { date: null };
  }
};

/**
 * Validate debug mode state
 * @returns Object containing validation results
 */
export const validateDebugMode = () => {
  const params = new URLSearchParams(window.location.search);
  const debugParam = params.get('debug');
  const debugDateRaw = safeStorage.getItem(DEBUG_DATE_KEY);
  const gameDateRaw = safeStorage.getItem(GAME_DATE_KEY);

  let debugDateParsed: Date | null = null;
  let gameDateParsed: Date | null = null;

  if (debugDateRaw) {
    const { date: parsed, migrateTo } = parseDateFromStorage(debugDateRaw);
    debugDateParsed = parsed;
    if (migrateTo && parsed) {
      // Migrate old ISO format to date-only for consistency
      safeStorage.setItem(DEBUG_DATE_KEY, migrateTo);
    }
  }

  if (gameDateRaw) {
    const { date: parsed } = parseDateFromStorage(gameDateRaw);
    gameDateParsed = parsed;
  }

  return {
    isDebugMode: debugParam === 'date',
    hasDebugDate: !!debugDateRaw,
    hasGameDate: !!gameDateRaw,
    datesMatch: !!(debugDateParsed && gameDateParsed && debugDateParsed.toDateString() === gameDateParsed.toDateString()),
    debugDateValid: !!debugDateParsed,
  };
};

/**
 * Safe wrapper for localStorage to handle quota exceeded errors
 */
const safeStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return null;
    }
  },
  
  setItem: (key: string, value: string): boolean => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error('Error writing to localStorage:', error);
      
      // If it's a quota error, try clearing old logs
      if (error instanceof DOMException && 
          (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')) {
        
        // For debug logs, truncate them instead of failing
        if (key === DEBUG_LOGS_KEY) {
          try {
            // Keep only the last half of the logs to free up space
            const truncatedValue = value.substring(value.length / 2);
            localStorage.setItem(key, truncatedValue);
            console.warn('Debug logs truncated due to storage limits');
            return true;
          } catch (truncateError) {
            console.error('Failed to truncate logs:', truncateError);
          }
        }
      }
      return false;
    }
  },
  
  removeItem: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return false;
    }
  }
};

/**
 * Configure and initialize the console logger
 */
const setupConsoleLogger = () => {
  if (typeof window === 'undefined') return;

  // Prevent multiple installations (HMR/route changes)
  if ((window as any).__BG_LOGGER_INSTALLED__) return;
  (window as any).__BG_LOGGER_INSTALLED__ = true;

  const maxLogEntries = 200; // Increased from 100
  const logs: string[] = [];

  // Save references to original console methods
  const originalConsole = {
    log: console.log,
    error: console.error,
    warn: console.warn,
    info: console.info
  };

  const saveLogs = () => {
    if (logs.length === 0) return;
    
    // Join logs to a single chunk and append to existing persisted logs
    const logsText = logs.join('\n');
    const existing = safeStorage.getItem(DEBUG_LOGS_KEY) || '';
    let combined = existing ? `${existing}\n${logsText}` : logsText;

    // Enforce size cap by trimming oldest characters
    if (combined.length > MAX_DEBUG_LOG_SIZE) {
      combined = combined.slice(combined.length - MAX_DEBUG_LOG_SIZE);
    }

    safeStorage.setItem(DEBUG_LOGS_KEY, combined);

    // Clear buffer after persisting to avoid duplicate appends
    logs.length = 0;
  };

  // Expose a flush helper for immediate persistence before copying
  (window as any).__BG_DEBUG_FLUSH__ = saveLogs;
  // Add log entry using circular buffer approach
  const addLogEntry = (entry: string) => {
    logs.push(entry);
    // Keep logs array at manageable size using circular buffer pattern
    if (logs.length > maxLogEntries) {
      logs.shift(); // Remove oldest log
    }
  };

  // Override console methods with better logging for transitions
  console.log = function(...args) {
    const debugParams = getDebugParams();
    const argString = args[0]?.toString() || '';
    
    // Always log animation, transition, and progression logs regardless of debug mode
    const isTransitionLog = argString.includes('🔄') || 
                            argString.includes('🏆') ||
                            argString.includes('🎬') ||
                            argString.includes('📝');
    
    // Always log debug date logs
    const isDebugDateLog = argString.includes('Debug date');
    
    // Show logs based on debug parameters
    if ((debugParams.showLogs || isTransitionLog || isDebugDateLog) && !argString.includes('🏆 STREAK')) {
      const logEntry = `${new Date().toISOString()} - log: ${JSON.stringify(args)}`;
      addLogEntry(logEntry);
      originalConsole.log.apply(console, args);
    }
  };

  console.error = function(...args) {
    const logEntry = `${new Date().toISOString()} - error: ${JSON.stringify(args)}`;
    addLogEntry(logEntry);
    originalConsole.error.apply(console, args);
  };

  console.warn = function(...args) {
    const logEntry = `${new Date().toISOString()} - warn: ${JSON.stringify(args)}`;
    addLogEntry(logEntry);
    originalConsole.warn.apply(console, args);
  };

  console.info = function(...args) {
    // Always show streak-related logs (marked with 🏆)
    const isStreakLog = args[0]?.toString().includes('🏆');
    if (isStreakLog || getDebugParams().showLogs) {
      const logEntry = `${new Date().toISOString()} - info: ${JSON.stringify(args)}`;
      addLogEntry(logEntry);
      originalConsole.info.apply(console, args);
    }
  };

  // Store logs periodically to reduce writes
  setInterval(saveLogs, 5000);

  // Persist before navigating/reloading or when tab becomes hidden
  window.addEventListener('beforeunload', saveLogs);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) saveLogs();
  });
};

// Initialize console logger
if (typeof window !== 'undefined') {
  setupConsoleLogger();
}

/**
 * Copy debug logs to clipboard
 * @returns Promise resolving to true if successful, false otherwise
 */
export const copyDebugLogs = async (): Promise<boolean> => {
  try {
    // Flush any buffered logs to storage before copying
    try { (window as any).__BG_DEBUG_FLUSH__?.(); } catch {}

    const logs = safeStorage.getItem(DEBUG_LOGS_KEY);
    if (!logs) {
      return false;
    }

    await navigator.clipboard.writeText(logs);
    return true;
  } catch (error) {
    console.error('Error copying debug logs:', error);
    return false;
  }
};

/**
 * Clear debug parameters from URL
 */
export const clearDebugParams = (): void => {
  if (typeof window === 'undefined') return;
  
  const url = new URL(window.location.href);
  url.searchParams.delete('debug');
  window.history.replaceState({}, document.title, url.toString());
};

/**
 * Get debug date from storage safely
 * @returns Debug date or null if not set or invalid
 */
export const getDebugDate = (): Date | null => {
  const debugDateStr = safeStorage.getItem(DEBUG_DATE_KEY);
  if (!debugDateStr) return null;
  
  try {
    const { date, migrateTo } = parseDateFromStorage(debugDateStr);
    if (!date) throw new Error('Invalid date format');

    if (migrateTo) {
      // Migrate old stored value to date-only format
      safeStorage.setItem(DEBUG_DATE_KEY, migrateTo);
      console.log('Migrated stored debug date to date-only format:', { from: debugDateStr, to: migrateTo });
    }

    console.log('Retrieved debug date from storage (date-only):', {
      stored: safeStorage.getItem(DEBUG_DATE_KEY),
      normalized: date.toISOString(),
      timestamp: new Date().toISOString()
    });
    return date;
  } catch (error) {
    console.error('Invalid debug date in storage:', error);
    safeStorage.removeItem(DEBUG_DATE_KEY);
    return null;
  }
};

/**
 * Set debug date with validation
 * @param date Date to set as debug date
 * @returns true if successful, false otherwise
 */
export const setDebugDate = (date: Date): boolean => {
  if (!date || isNaN(date.getTime())) {
    console.error('Attempted to set invalid debug date');
    return false;
  }
  
  try {
    const normalizedDate = normalizeDate(date);
    const stored = formatDate(normalizedDate);
    
    console.log('Setting debug date (date-only storage):', {
      original: date.toISOString(),
      normalized: normalizedDate.toISOString(),
      stored,
      timestamp: new Date().toISOString()
    });
    
    // Store as YYYY-MM-DD (local date semantics)
    return safeStorage.setItem(DEBUG_DATE_KEY, stored);
  } catch (error) {
    console.error('Failed to set debug date:', error);
    toast.error('Debug mode: Failed to set date');
    return false;
  }
};
